import * as THREE from 'three';

/**
 * Texture sources with hard timeouts.
 * Remote hosts that hang or 403 (e.g. solarsystemscope) previously blocked the splash screen forever.
 * We use threex.planets + three.js CDN maps, falling back to procedural canvases.
 */

const THREEX =
  'https://raw.githubusercontent.com/jeromeetienne/threex.planets/master/images';
const THREE_PLANETS =
  'https://cdn.jsdelivr.net/gh/mrdoob/three.js@r172/examples/textures/planets';

const LOAD_TIMEOUT_MS = 4000;

/** Primary remote maps. Failures / timeouts fall back to procedural surfaces. */
export const TEXTURE_URLS: Record<string, string> = {
  sun: `${THREEX}/sunmap.jpg`,
  mercury: `${THREEX}/mercurymap.jpg`,
  venus: `${THREEX}/venusmap.jpg`,
  earth: `${THREE_PLANETS}/earth_atmos_2048.jpg`,
  earthClouds: `${THREE_PLANETS}/earth_clouds_1024.png`,
  moon: `${THREE_PLANETS}/moon_1024.jpg`,
  mars: `${THREEX}/marsmap1k.jpg`,
  jupiter: `${THREEX}/jupitermap.jpg`,
  saturn: `${THREEX}/saturnmap.jpg`,
  uranus: `${THREEX}/uranusmap.jpg`,
  neptune: `${THREEX}/neptunemap.jpg`,
};

const cache = new Map<string, THREE.Texture>();
const loader = new THREE.TextureLoader();
loader.crossOrigin = 'anonymous';

function makeProcedural(
  base: string,
  opts: { noise?: number; bands?: boolean; ice?: boolean } = {},
): THREE.CanvasTexture {
  const size = 512;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const { r, g, b } = hexToRgb(base);

  const grad = ctx.createLinearGradient(0, 0, 0, size);
  if (opts.bands) {
    for (let i = 0; i <= 12; i++) {
      const t = i / 12;
      const f = 0.75 + 0.25 * Math.sin(t * Math.PI * 8);
      grad.addColorStop(
        t,
        `rgb(${clamp(r * f)},${clamp(g * f * 0.95)},${clamp(b * f * 0.9)})`,
      );
    }
  } else if (opts.ice) {
    grad.addColorStop(0, `rgba(${clamp(r + 40)},${clamp(g + 40)},${clamp(b + 50)},1)`);
    grad.addColorStop(0.5, `rgb(${r},${g},${b})`);
    grad.addColorStop(1, `rgb(${clamp(r - 20)},${clamp(g - 15)},${clamp(b)})`);
  } else {
    grad.addColorStop(0, `rgb(${clamp(r + 30)},${clamp(g + 25)},${clamp(b + 20)})`);
    grad.addColorStop(0.5, `rgb(${r},${g},${b})`);
    grad.addColorStop(1, `rgb(${clamp(r - 25)},${clamp(g - 25)},${clamp(b - 20)})`);
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  const noise = opts.noise ?? 0.15;
  const img = ctx.getImageData(0, 0, size, size);
  for (let i = 0; i < img.data.length; i += 4) {
    const n = (Math.random() - 0.5) * 255 * noise;
    img.data[i] = clamp(img.data[i] + n);
    img.data[i + 1] = clamp(img.data[i + 1] + n);
    img.data[i + 2] = clamp(img.data[i + 2] + n);
  }
  if (!opts.bands && !opts.ice) {
    for (let c = 0; c < 40; c++) {
      const cx = Math.random() * size;
      const cy = Math.random() * size;
      const rad = 2 + Math.random() * 12;
      ctx.beginPath();
      ctx.arc(cx, cy, rad, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(0,0,0,${0.08 + Math.random() * 0.15})`;
      ctx.fill();
    }
  }
  ctx.putImageData(img, 0, 0);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.anisotropy = 4;
  return tex;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function clamp(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)));
}

const PROCEDURAL_HINTS: Record<string, { color: string; bands?: boolean; ice?: boolean }> = {
  sun: { color: '#ffcc44' },
  mercury: { color: '#9a9a9a' },
  venus: { color: '#e8cda0' },
  earth: { color: '#3a6a9a' },
  earthClouds: { color: '#ffffff' },
  moon: { color: '#b0b0b0' },
  mars: { color: '#c1440e' },
  jupiter: { color: '#c88b3a', bands: true },
  saturn: { color: '#e6d5a8', bands: true },
  uranus: { color: '#b0e0e6', ice: true },
  neptune: { color: '#3f54ba', ice: true },
  pluto: { color: '#c4a882' },
  ceres: { color: '#a0a0a0' },
  haumea: { color: '#d8d0c8', ice: true },
  makemake: { color: '#c8b090' },
  eris: { color: '#e0e0e8', ice: true },
  io: { color: '#e8d060' },
  europa: { color: '#d4c8a8', ice: true },
  ganymede: { color: '#a89880' },
  callisto: { color: '#6a6058' },
  titan: { color: '#c8a050' },
  enceladus: { color: '#f0f4f8', ice: true },
  mimas: { color: '#c8c8c8' },
  tethys: { color: '#d8d8d0', ice: true },
  dione: { color: '#c8c8c0' },
  rhea: { color: '#b8b8b0' },
  iapetus: { color: '#6a5a48' },
  triton: { color: '#c0b8b0', ice: true },
  charon: { color: '#a09890' },
  miranda: { color: '#b0b0a8' },
  ariel: { color: '#c0c0b8', ice: true },
  umbriel: { color: '#606060' },
  titania: { color: '#a8a098' },
  oberon: { color: '#989088' },
  phobos: { color: '#8a7a6a' },
  deimos: { color: '#9a8a7a' },
  rocky: { color: '#8a8070' },
};

export function getProcedural(key: string, fallbackColor = '#888888'): THREE.Texture {
  const hint = PROCEDURAL_HINTS[key] ?? { color: fallbackColor };
  const cacheKey = `proc:${key}`;
  let t = cache.get(cacheKey);
  if (!t) {
    t = makeProcedural(hint.color, hint);
    cache.set(cacheKey, t);
  }
  return t;
}

function loadImageTexture(url: string): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (t) => resolve(t),
      undefined,
      (err) => reject(err ?? new Error(`Failed to load ${url}`)),
    );
  });
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const id = window.setTimeout(() => reject(new Error(`Timeout loading ${label}`)), ms);
    promise.then(
      (v) => {
        clearTimeout(id);
        resolve(v);
      },
      (e) => {
        clearTimeout(id);
        reject(e);
      },
    );
  });
}

export async function loadTexture(
  key: string,
  fallbackColor = '#888888',
  onProgress?: () => void,
): Promise<THREE.Texture> {
  if (cache.has(key)) {
    onProgress?.();
    return cache.get(key)!;
  }

  const url = TEXTURE_URLS[key];
  if (!url) {
    const t = getProcedural(key, fallbackColor);
    cache.set(key, t);
    onProgress?.();
    return t;
  }

  try {
    const tex = await withTimeout(loadImageTexture(url), LOAD_TIMEOUT_MS, key);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    cache.set(key, tex);
    onProgress?.();
    return tex;
  } catch (err) {
    console.warn(`[SkyTrek] Texture "${key}" unavailable, using procedural.`, err);
    const t = getProcedural(key, fallbackColor);
    cache.set(key, t);
    onProgress?.();
    return t;
  }
}

export function makeRingTexture(color = '#c9b896'): THREE.CanvasTexture {
  const w = 1024;
  const h = 64;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  const { r, g, b } = hexToRgb(color);
  const img = ctx.createImageData(w, h);
  for (let x = 0; x < w; x++) {
    const u = x / w;
    let alpha = 0;
    if (u > 0.05 && u < 0.98) {
      alpha = 0.15 + 0.7 * Math.pow(Math.sin(u * Math.PI), 0.6);
      if (u > 0.42 && u < 0.48) alpha *= 0.08;
      if (u > 0.62 && u < 0.64) alpha *= 0.25;
      if (u > 0.78 && u < 0.8) alpha *= 0.35;
      alpha *= 0.85 + 0.15 * Math.sin(u * 80);
    }
    for (let y = 0; y < h; y++) {
      const i = (y * w + x) * 4;
      const n = (Math.random() - 0.5) * 20;
      img.data[i] = clamp(r + n);
      img.data[i + 1] = clamp(g + n);
      img.data[i + 2] = clamp(b + n * 0.5);
      img.data[i + 3] = clamp(alpha * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  return tex;
}

export function makeStarfieldTexture(size = 2048): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#02040a';
  ctx.fillRect(0, 0, size, size);

  const band = ctx.createLinearGradient(0, size * 0.35, 0, size * 0.65);
  band.addColorStop(0, 'rgba(40,50,90,0)');
  band.addColorStop(0.5, 'rgba(60,70,120,0.25)');
  band.addColorStop(1, 'rgba(40,50,90,0)');
  ctx.fillStyle = band;
  ctx.fillRect(0, 0, size, size);

  for (let i = 0; i < 8000; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const bright = Math.random();
    const r = bright > 0.97 ? 1.8 : bright > 0.9 ? 1.2 : 0.6;
    const a = 0.3 + bright * 0.7;
    const hue = Math.random() > 0.85 ? (Math.random() > 0.5 ? '#aaccff' : '#ffddaa') : '#ffffff';
    ctx.beginPath();
    ctx.fillStyle =
      hue === '#ffffff'
        ? `rgba(255,255,255,${a})`
        : hue === '#aaccff'
          ? `rgba(170,200,255,${a})`
          : `rgba(255,220,170,${a})`;
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.strokeStyle = 'rgba(180,200,255,0.06)';
  ctx.lineWidth = 1;
  for (let c = 0; c < 12; c++) {
    const pts: [number, number][] = [];
    let x = Math.random() * size;
    let y = Math.random() * size;
    const n = 4 + Math.floor(Math.random() * 5);
    for (let i = 0; i < n; i++) {
      pts.push([x, y]);
      x += (Math.random() - 0.5) * 80;
      y += (Math.random() - 0.5) * 80;
    }
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.stroke();
    for (const [px, py] of pts) {
      ctx.beginPath();
      ctx.fillStyle = 'rgba(220,230,255,0.55)';
      ctx.arc(px, py, 1.2, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.mapping = THREE.EquirectangularReflectionMapping;
  return tex;
}

export function makeNebulaSprite(
  color: string,
  size = 256,
): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const { r, g, b } = hexToRgb(color);
  const g1 = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g1.addColorStop(0, `rgba(${r},${g},${b},0.45)`);
  g1.addColorStop(0.4, `rgba(${r},${g},${b},0.15)`);
  g1.addColorStop(1, `rgba(${r},${g},${b},0)`);
  ctx.fillStyle = g1;
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 8; i++) {
    const x = size * (0.3 + Math.random() * 0.4);
    const y = size * (0.3 + Math.random() * 0.4);
    const rad = size * (0.1 + Math.random() * 0.2);
    const g2 = ctx.createRadialGradient(x, y, 0, x, y, rad);
    g2.addColorStop(0, `rgba(${r},${g},${b},0.2)`);
    g2.addColorStop(1, `rgba(${r},${g},${b},0)`);
    ctx.fillStyle = g2;
    ctx.fillRect(0, 0, size, size);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
